import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  X, Save, Camera, FileText, Paperclip, Loader, CheckCircle, Trash2,
  ExternalLink, Lock, ShieldCheck, Sparkles, DollarSign, Pencil,
} from 'lucide-react';
import { uprightJpegBytes } from '../utils/imageOrient';
import ConditionReportDrawer from './ConditionReportDrawer';

// ── Private, admin-only vehicle documents ─────────────────────────────────────
// Rego papers / purchase invoice / insurance / finance-lease contracts for a fleet
// vehicle. Scanned on the device camera (multi-page → one PDF, like the Service &
// Repairs page), read by Claude to auto-classify the document AND pull recordable
// data (purchase price, start kms, service interval, a suggested weekly lease fee),
// then stored OWNER-ONLY in Drive. Files are NEVER public-linked — they're served
// back only through the cookie-gated /vehicle-doc function to an admin session.

// Vehicle doc/profile metadata is admin-only — it goes through the cookie-gated
// /vehicle-admin function (same hp_auth/hp_portal admin check as /vehicle-doc),
// NOT the open /appsscript proxy where any logged-in session could read prices.
const VEHICLE_ADMIN_URL = '/.netlify/functions/vehicle-admin-sb';
const VEHICLE_SERVICE_URL = '/.netlify/functions/vehicle-service-sb';
const MAX_DOC_MB = 4; // base64 inflates ~33%; Netlify function body limit is 6 MB

const DOC_CATEGORIES = [
  'Registration', 'Insurance', 'Contract of Sale', 'Purchase Invoice', 'Finance / Lease',
  'Service', 'Condition Report', 'Roadworthy / Inspection', 'Warranty', 'Other',
];

const CATEGORY_COLORS = {
  'Registration':            'var(--hp-gold)',
  'Insurance':               'var(--hp-blue)',
  'Contract of Sale':        '#0ea5e9',
  'Purchase Invoice':        'var(--hp-green)',
  'Finance / Lease':         '#7c3aed',
  'Service':                 'var(--hp-red)',
  'Condition Report':        '#14b8a6',
  'Roadworthy / Inspection': 'var(--hp-amber)',
  'Warranty':                'var(--hp-blue)',
  'Other':                   'var(--hp-dim)',
};

const toLocalISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayISO = () => toLocalISO(new Date());

const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 });

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
  reader.onerror = () => reject(new Error('Could not read file'));
  reader.readAsDataURL(file);
});

// Deterministic fallback when Claude can't see a price: amortise the purchase over
// ~5 years (260 weeks) and add a flat running-cost allowance (rego + insurance +
// servicing). A rough starting point only — the admin edits the figure.
function computeWeeklyLease(purchasePrice) {
  const p = Number(purchasePrice) || 0;
  if (p <= 0) return 0;
  return Math.round(p / 260 + 60);
}

const EXTRACT_SCHEMA = {
  type: 'object',
  // Required by the Messages API structured-outputs (output_config.format) schema:
  // every object must explicitly set additionalProperties:false, and every
  // property must be listed in `required` (see below). Omitting this 400s with
  // "output_config.format.schema: additionalProperties must be explicitly set to false".
  additionalProperties: false,
  properties: {
    docCategory:           { type: 'string', enum: DOC_CATEGORIES, description: 'Best-fit category for this document.' },
    docLabel:              { type: 'string', description: 'Short human label, e.g. "2025 Registration Renewal" or "Tax Invoice — purchase". Empty string if unclear.' },
    rego:                  { type: 'string', description: 'Vehicle registration / plate, uppercase no spaces. Empty string if not shown.' },
    make:                  { type: 'string', description: 'Vehicle make if shown, else empty string.' },
    model:                 { type: 'string', description: 'Vehicle model if shown, else empty string.' },
    year:                  { type: 'string', description: 'Vehicle build year if shown, else empty string.' },
    purchasePrice:         { type: 'number', description: 'Total vehicle purchase price in AUD incl GST. 0 if this is not a purchase/sale document.' },
    purchaseDate:          { type: 'string', description: 'Purchase / invoice date as YYYY-MM-DD, else empty string.' },
    startKms:              { type: 'number', description: 'Odometer reading at purchase / handover in km. 0 if not shown.' },
    regoExpiry:            { type: 'string', description: 'Registration expiry date YYYY-MM-DD if shown, else empty string.' },
    insuranceExpiry:       { type: 'string', description: 'Insurance expiry / renewal date YYYY-MM-DD if shown, else empty string.' },
    serviceIntervalKm:     { type: 'number', description: 'Manufacturer-recommended service interval in km for this make/model. Use your own knowledge of the vehicle if it is not printed. 0 if unknown.' },
    serviceIntervalMonths: { type: 'number', description: 'Manufacturer-recommended service interval in months. Use your own knowledge if not printed. 0 if unknown.' },
    suggestedWeeklyLeaseFee:{ type: 'number', description: 'A fair WEEKLY lease fee in AUD for a driver to use this van — estimate from the purchase price amortised over ~5 years plus running costs (rego, insurance, servicing). 0 if you cannot estimate.' },
    leaseFeeReasoning:     { type: 'string', description: 'One short sentence explaining how the weekly lease fee was estimated.' },
    summary:               { type: 'string', description: 'One short sentence describing the document.' },
    // ── Service / repair invoice fields (only meaningful when docCategory === 'Service') ──
    jobType:               { type: 'string', enum: ['Scheduled Service', 'Repair', 'Tyres', 'Brakes', 'Battery', 'Windscreen', 'Accident / Insurance', 'Pink Slip / Inspection', 'Breakdown', 'Other'], description: 'If this is a service/repair invoice, the best-fit job category; else "Other".' },
    serviceProvider:       { type: 'string', description: 'For a service/repair invoice: the trading name of the workshop / service centre that ISSUED it — read from the letterhead, logo or payee. NOT the vehicle make and NOT a default. Empty string otherwise.' },
    serviceDate:           { type: 'string', description: 'For a service/repair invoice: the service / invoice date YYYY-MM-DD. Empty string otherwise.' },
    serviceOdometer:       { type: 'number', description: 'For a service/repair invoice: the odometer/km reading at the service. 0 otherwise.' },
    serviceInvoiceNo:      { type: 'string', description: 'For a service/repair invoice: the invoice number/reference. Empty string otherwise.' },
    serviceTotalExGst:     { type: 'number', description: 'For a service/repair invoice: total EXCLUDING GST in AUD. 0 otherwise.' },
    serviceTotalIncGst:    { type: 'number', description: 'For a service/repair invoice: total INCLUDING GST in AUD. 0 otherwise.' },
    serviceNextDate:       { type: 'string', description: 'For a service/repair invoice: the next service DUE date YYYY-MM-DD if printed, else empty string.' },
    serviceNextKm:         { type: 'number', description: 'For a service/repair invoice: the next service DUE odometer in km if printed, else 0.' },
  },
  required: ['docCategory', 'docLabel', 'rego', 'make', 'model', 'year', 'purchasePrice', 'purchaseDate', 'startKms', 'regoExpiry', 'insuranceExpiry', 'serviceIntervalKm', 'serviceIntervalMonths', 'suggestedWeeklyLeaseFee', 'leaseFeeReasoning', 'summary', 'jobType', 'serviceProvider', 'serviceDate', 'serviceOdometer', 'serviceInvoiceNo', 'serviceTotalExGst', 'serviceTotalIncGst', 'serviceNextDate', 'serviceNextKm'],
};

async function extractVehicleDocFields(file, vehicle) {
  const keyResp = await fetch('/.netlify/functions/get-claude-key');
  const { key } = await keyResp.json();
  if (!key) throw new Error('Could not get API key');

  const data = await fileToBase64(file);
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  const docBlock = isPdf
    ? { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data } }
    : { type: 'image', source: { type: 'base64', media_type: file.type || 'image/jpeg', data } };

  const ctx = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');
  const resp = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': key,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: 'claude-opus-4-8',
      max_tokens: 1500,
      output_config: { format: { type: 'json_schema', schema: EXTRACT_SCHEMA } },
      messages: [{
        role: 'user',
        content: [
          docBlock,
          { type: 'text', text: `Today's date is ${todayISO()}. This is a document for a fleet vehicle (registration papers, a dealer contract of sale, a purchase/tax invoice, an insurance certificate, a finance/lease contract, or a service/repair workshop invoice). The vehicle is ${ctx || 'unknown'}${vehicle.rego ? `, rego ${vehicle.rego}` : ''}. Classify the document and extract any recordable data. A signed dealer/vendor sale agreement or vehicle order form is "Contract of Sale"; a tax invoice or receipt for the purchase is "Purchase Invoice" — both carry the purchase price and date. If it is a service or repair invoice from a mechanic/workshop, set docCategory to "Service" and fill the service fields — read the issuing workshop's name from the actual letterhead/logo on THIS document (different jobs come from different service centres, so never assume a default or reuse a name that isn't printed here). For service intervals, use the manufacturer's recommended schedule for this make/model from your own knowledge if it isn't printed on the document. Dates without a year belong to the current or most recent past year, never the future. All amounts are AUD.` },
        ],
      }],
    }),
  });
  if (!resp.ok) {
    const errText = await resp.text();
    throw new Error(`Claude error ${resp.status}: ${errText.substring(0, 150)}`);
  }
  const result = await resp.json();
  const raw = (result.content || []).filter(b => b.type === 'text').map(b => b.text).join('');
  return JSON.parse(raw);
}

// ── small style helpers (match the Fleet / Service drawers) ───────────────────
const labelStyle = {
  fontSize: '.7rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)',
  textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5,
};
const inputStyle = { color: 'var(--hp-text)', background: 'var(--hp-surface)', width: '100%' };

function SectionTitle({ n, title, hint }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, marginTop: n > 1 ? 24 : 0 }}>
      <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--hp-gold)', color: '#000', fontSize: '.65rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{n}</div>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--hp-gold)', fontWeight: 700 }}>{title}</span>
      {hint && <span style={{ fontSize: '.72rem', color: 'var(--hp-muted)' }}>{hint}</span>}
    </div>
  );
}

function CategoryBadge({ category }) {
  const color = CATEGORY_COLORS[category] || 'var(--hp-dim)';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', fontWeight: 700,
      fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.04em',
      padding: '2px 8px', borderRadius: 4, background: `${color}22`, color,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />
      {category || 'Other'}
    </span>
  );
}

const normReg = (s) => (s || '').toUpperCase().replace(/[\s-]/g, '');

// `vehicle`  — fixed target (opened from a fleet row). Locks the drawer to it.
// `vehicles` — when no `vehicle` is passed, the drawer opens in "picker" mode:
//              scan first, and the OCR-read rego auto-assigns the doc to the
//              matching fleet vehicle (with a manual override dropdown).
export default function VehicleDocsDrawer({ vehicle, vehicles, currentUser, onClose, addNotification }) {
  const pickerMode = !vehicle;
  const [selectedVehicle, setSelectedVehicle] = useState(vehicle || null);
  const activeVehicle = selectedVehicle || {};
  const rego = (activeVehicle.rego || '').toUpperCase();
  const lastExtractRef = useRef(null);
  const ocrSeqRef = useRef(0); // guards overlapping reads: only the latest scan's result applies

  const vehicleOptions = useMemo(() => (
    [...(vehicles || [])]
      .filter(v => v.rego)
      .sort((a, b) => (a.rego || '').localeCompare(b.rego || ''))
  ), [vehicles]);

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Scan / upload state (mirrors the Service & Repairs invoice flow)
  const [pdfFile, setPdfFile] = useState(null);
  const [scanPages, setScanPages] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  // Documents arrive together — a contract of sale AND its tax invoice. Dropping both
  // loads the first for review and holds the rest here, one filed after the other,
  // instead of quietly keeping only the file the browser happened to list first.
  const [queue, setQueue] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [extractedNote, setExtractedNote] = useState('');
  const [category, setCategory] = useState('Other');
  const [label, setLabel] = useState('');
  // For a scanned Service, whether the resulting service-history record is exposed
  // to the van's current driver ('operator') or kept admin-only ('admin').
  const [serviceVisibility, setServiceVisibility] = useState('admin');
  const [lastExtract, setLastExtract] = useState(null);
  const [savingDoc, setSavingDoc] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [crOpen, setCrOpen] = useState(null);   // { existing: null } = new · { existing:{docId,report} } = edit
  const fileInputRef = useRef(null);

  // Vehicle economics (profile)
  const [profile, setProfile] = useState({
    purchasePrice: '', purchaseDate: '', startKms: '',
    serviceIntervalKm: '', serviceIntervalMonths: '',
    suggestedLeaseFee: '', leaseFeeNotes: '',
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const setP = (field, val) => setProfile(prev => ({ ...prev, [field]: val }));

  const api = async (payload) => {
    const res = await fetch(VEHICLE_ADMIN_URL, { method: 'POST', body: JSON.stringify(payload) });
    return res.json();
  };

  // Load existing docs + profile for this vehicle
  const loadDocs = async () => {
    const d = await api({ action: 'getVehicleDocuments', rego });
    if (d.success) setDocs(d.documents || []);
  };
  useEffect(() => {
    let alive = true;
    // Picker mode before a vehicle is chosen — nothing to load yet.
    if (!rego) {
      setDocs([]);
      setProfile({
        purchasePrice: '', purchaseDate: '', startKms: '',
        serviceIntervalKm: '', serviceIntervalMonths: '',
        suggestedLeaseFee: '', leaseFeeNotes: '',
      });
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const [d, p] = await Promise.all([
          api({ action: 'getVehicleDocuments', rego }),
          api({ action: 'getVehicleProfile', rego }),
        ]);
        if (!alive) return;
        if (d.success) setDocs(d.documents || []);
        setProfile(p.success && p.profile ? {
          purchasePrice: p.profile.purchasePrice || '',
          purchaseDate: p.profile.purchaseDate || '',
          startKms: p.profile.startKms || '',
          serviceIntervalKm: p.profile.serviceIntervalKm || '',
          serviceIntervalMonths: p.profile.serviceIntervalMonths || '',
          suggestedLeaseFee: p.profile.suggestedLeaseFee || '',
          leaseFeeNotes: p.profile.leaseFeeNotes || '',
        } : {
          purchasePrice: '', purchaseDate: '', startKms: '',
          serviceIntervalKm: '', serviceIntervalMonths: '',
          suggestedLeaseFee: '', leaseFeeNotes: '',
        });
        // If this vehicle was just auto-selected from a scan, re-apply the OCR
        // fields into any empty profile fields (the load above resets them).
        if (lastExtractRef.current) fillProfileFromExtract(lastExtractRef.current);
      } catch (e) {
        addNotification(`Could not load documents: ${e.message}`, 'error');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [rego]);

  // Fill an empty profile field only — never overwrite what's already there.
  const fillProfileFromExtract = (x) => {
    setProfile(prev => {
      const next = { ...prev };
      const fill = (field, value) => {
        if (value === '' || value === 0 || value == null) return;
        if (prev[field] !== '' && prev[field] !== 0 && prev[field] != null) return;
        next[field] = value;
      };
      fill('purchasePrice', x.purchasePrice);
      fill('purchaseDate', x.purchaseDate);
      fill('startKms', x.startKms);
      fill('serviceIntervalKm', x.serviceIntervalKm);
      fill('serviceIntervalMonths', x.serviceIntervalMonths);
      const fee = x.suggestedWeeklyLeaseFee > 0 ? x.suggestedWeeklyLeaseFee : computeWeeklyLease(x.purchasePrice);
      fill('suggestedLeaseFee', fee || '');
      if (!prev.leaseFeeNotes && x.leaseFeeReasoning) next.leaseFeeNotes = x.leaseFeeReasoning;
      return next;
    });
  };

  const autoClassify = async (f) => {
    const seq = ++ocrSeqRef.current; // this read supersedes any earlier in-flight one
    setExtracting(true);
    setExtractedNote('');
    try {
      const x = await extractVehicleDocFields(f, activeVehicle);
      if (seq !== ocrSeqRef.current) return; // a newer scan started — drop this stale result
      setLastExtract(x);
      lastExtractRef.current = x;
      if (DOC_CATEGORIES.includes(x.docCategory)) setCategory(x.docCategory);
      if (x.docLabel) setLabel(prev => prev || x.docLabel);
      fillProfileFromExtract(x);
      const bits = [];
      if (x.docCategory) bits.push(`filed as ${x.docCategory}`);
      if (x.purchasePrice > 0) bits.push(`price ${fmtMoney(x.purchasePrice)}`);
      if (x.startKms > 0) bits.push(`${Number(x.startKms).toLocaleString('en-AU')} km`);
      if (x.serviceIntervalKm > 0) bits.push(`service every ${Number(x.serviceIntervalKm).toLocaleString('en-AU')} km`);
      const fee = x.suggestedWeeklyLeaseFee > 0 ? x.suggestedWeeklyLeaseFee : computeWeeklyLease(x.purchasePrice);
      if (fee > 0) bits.push(`lease ~${fmtMoney(fee)}/wk`);
      setExtractedNote(bits.length ? `Read by OCR: ${bits.join(' · ')} — check before saving` : 'Document read — classify and save below');
      if (pickerMode) {
        // Scan-first flow — assign the doc to the fleet vehicle whose rego the OCR read.
        if (x.rego) {
          const match = vehicleOptions.find(v => normReg(v.rego) === normReg(x.rego));
          if (match) {
            setSelectedVehicle(match);
            addNotification(`Matched to ${match.rego} — check and save`, 'success');
          } else {
            addNotification(`Scan shows rego ${x.rego}, not in the fleet — pick the vehicle below`, 'error');
          }
        } else {
          addNotification('No rego found on the scan — pick the vehicle below', 'error');
        }
      } else if (x.rego && normReg(x.rego) !== normReg(rego)) {
        addNotification(`Heads up: this document shows rego ${x.rego}, not ${rego}`, 'error');
      }
    } catch (e) {
      if (seq !== ocrSeqRef.current) return;
      setExtractedNote('');
      addNotification(`Couldn't read the document automatically (${e.message}) — pick a category and save, the file still attaches`, 'error');
    } finally {
      if (seq === ocrSeqRef.current) setExtracting(false);
    }
  };

  // Why a file can't be attached, or null if it can. Checked before anything is
  // queued, so a rejected file never sits in the queue and stalls the batch.
  const fileProblem = (f) => {
    if (!f) return 'no file';
    const okType = f.type === 'application/pdf' || f.type.startsWith('image/') || /\.pdf$/i.test(f.name);
    if (!okType) return `${f.name}: only PDF or image files can be attached`;
    if (f.size > MAX_DOC_MB * 1024 * 1024) return `${f.name} is too large — max ${MAX_DOC_MB} MB`;
    return null;
  };

  const acceptFile = (f) => {
    const problem = fileProblem(f);
    if (problem) { addNotification(problem, 'error'); return false; }
    setScanPages([]);
    setPdfFile(f);
    autoClassify(f);
    return true;
  };

  // Take a whole dropped/picked batch: the first file opens for review, the rest wait.
  const acceptFiles = (list) => {
    const all = Array.from(list || []);
    if (!all.length) return;
    // Reject the unusable ones up front rather than letting one stall the batch later.
    const files = [];
    all.forEach(f => { const problem = fileProblem(f); if (problem) addNotification(problem, 'error'); else files.push(f); });
    if (!files.length) return;
    // If a document is already open for review, the whole drop joins the queue behind it.
    const rest = pdfFile ? files : files.slice(1);
    if (!pdfFile) acceptFile(files[0]);
    if (rest.length) {
      setQueue(prev => [...prev, ...rest]);
      addNotification(`${rest.length} file${rest.length === 1 ? '' : 's'} queued — filed one after the other`, 'success');
    }
  };

  const handleFilePick = (e) => { acceptFiles(e.target.files); e.target.value = ''; };
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); acceptFiles(e.dataTransfer?.files); };

  // Multi-page camera scan → one combined PDF (pdf-lib, lazy-loaded), same as Service.
  const addScanPage = async (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) { addNotification('That wasn’t a photo — use the camera', 'error'); return; }
    if (f.size > MAX_DOC_MB * 1024 * 1024) { addNotification(`Page too large — max ${MAX_DOC_MB} MB`, 'error'); return; }
    const pages = [...scanPages, f];
    setScanPages(pages);
    try {
      const { PDFDocument } = await import('pdf-lib');
      const pdf = await PDFDocument.create();
      for (const img of pages) {
        const upright = await uprightJpegBytes(img); // apply EXIF orientation (iPhone fix)
        let embedded;
        if (upright) {
          embedded = await pdf.embedJpg(upright);
        } else {
          const buf = await img.arrayBuffer();
          embedded = img.type === 'image/png' ? await pdf.embedPng(buf) : await pdf.embedJpg(buf);
        }
        const page = pdf.addPage([embedded.width, embedded.height]);
        page.drawImage(embedded, { x: 0, y: 0, width: embedded.width, height: embedded.height });
      }
      const bytes = await pdf.save();
      const combined = new File([bytes], `${rego}-doc-${pages.length}p.pdf`, { type: 'application/pdf' });
      setPdfFile(combined);
      // Don't OCR yet — the user reads the whole multi-page PDF once, when finished scanning,
      // via the "Read all pages" button below. That way every page is read, not just page 1.
      setExtractedNote('');
    } catch (e) {
      addNotification('Could not build the PDF from that photo — ' + e.message, 'error');
    }
  };
  const handleScanPick = (e) => { addScanPage(e.target.files?.[0]); e.target.value = ''; };

  const clearScan = () => { setPdfFile(null); setScanPages([]); setExtractedNote(''); setLabel(''); setLastExtract(null); lastExtractRef.current = null; setServiceVisibility('admin'); };

  const buildExtractMeta = () => {
    if (!lastExtract) return null;
    const x = lastExtract;
    return {
      purchasePrice: x.purchasePrice || 0, purchaseDate: x.purchaseDate || '',
      startKms: x.startKms || 0, regoExpiry: x.regoExpiry || '', insuranceExpiry: x.insuranceExpiry || '',
      serviceIntervalKm: x.serviceIntervalKm || 0, serviceIntervalMonths: x.serviceIntervalMonths || 0,
      suggestedWeeklyLeaseFee: x.suggestedWeeklyLeaseFee || 0, leaseFeeReasoning: x.leaseFeeReasoning || '',
      summary: x.summary || '',
    };
  };

  const profilePayload = () => ({
    action: 'saveVehicleProfile', rego,
    purchasePrice: Number(profile.purchasePrice) || 0,
    purchaseDate: profile.purchaseDate || '',
    startKms: Number(profile.startKms) || 0,
    serviceIntervalKm: Number(profile.serviceIntervalKm) || 0,
    serviceIntervalMonths: Number(profile.serviceIntervalMonths) || 0,
    suggestedLeaseFee: Number(profile.suggestedLeaseFee) || 0,
    leaseFeeBasis: 'weekly',
    leaseFeeNotes: profile.leaseFeeNotes || '',
    updatedBy: currentUser?.login || '',
  });

  const handleSaveProfile = async () => {
    if (!rego) { addNotification('Pick or scan a vehicle first', 'error'); return; }
    setSavingProfile(true);
    try {
      const d = await api(profilePayload());
      if (d.success) addNotification(`${rego} — vehicle details saved`, 'success');
      else addNotification(`Save failed: ${d.error || 'unknown error'}`, 'error');
    } catch (e) {
      addNotification(`Error: ${e.message}`, 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  // Create the vehicle_service record for a scanned Service invoice, then attach the
  // same PDF to it. Returns {ok, error}; never throws (a doc save must not be undone
  // by a service-history hiccup — the file is already safely in Documents).
  const fileServiceRecord = async (fileBase64, documentId) => {
    try {
      const x = lastExtract || {};
      const cost = x.serviceTotalExGst > 0 ? x.serviceTotalExGst
        : x.serviceTotalIncGst > 0 ? Math.round((x.serviceTotalIncGst / 1.1) * 100) / 100 : 0;
      const svcRes = await fetch(VEHICLE_SERVICE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'saveVehicleService',
          record: {
            rego,
            type: x.jobType || 'Scheduled Service',
            status: 'Completed',
            dateIn: x.serviceDate || '', dateOut: x.serviceDate || '',
            odometer: x.serviceOdometer || 0,
            provider: x.serviceProvider || '',
            description: x.summary || label || '',
            cost, invoiceNo: x.serviceInvoiceNo || '',
            nextServiceDate: x.serviceNextDate || '', nextServiceKm: x.serviceNextKm || 0,
            visibility: serviceVisibility,
            documentId: documentId || '',
          },
        }),
      });
      const svc = await svcRes.json();
      if (!svc.success) return { ok: false, error: svc.error || 'save failed' };
      // Attach the invoice PDF to the new service record.
      const fileRes = await fetch(VEHICLE_SERVICE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'saveVehicleServiceFile',
          recordId: svc.id, rego,
          pdfBase64: fileBase64,
          filename: pdfFile.name,
          contentType: pdfFile.type || 'application/pdf',
        }),
      });
      const file = await fileRes.json();
      if (!file.success) return { ok: false, error: 'record saved but invoice attach failed: ' + (file.error || '') };
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  };

  const handleSaveDoc = async () => {
    if (!pdfFile) { addNotification('Scan or choose a file first', 'error'); return; }
    if (!rego) { addNotification('Assign this document to a vehicle first', 'error'); return; }
    setSavingDoc(true);
    try {
      const fileBase64 = await fileToBase64(pdfFile);
      const d = await api({
        action: 'saveVehicleDocument',
        rego,
        category,
        label: label || category,
        filename: pdfFile.name,
        contentType: pdfFile.type || 'application/pdf',
        fileBase64,
        extracted: buildExtractMeta(),
        uploadedBy: currentUser?.login || '',
      });
      if (!d.success) { addNotification(`Upload failed: ${d.error || 'unknown error'}`, 'error'); return; }
      // Persist any economics the OCR pulled, so they aren't lost.
      try { await api(profilePayload()); } catch (e) { /* non-fatal */ }
      // A Service also lands on the vehicle's service-history record (with the same
      // invoice attached), visible per the admin's exposed/hidden choice.
      if (category === 'Service') {
        const svc = await fileServiceRecord(fileBase64, d.id);
        if (svc.ok) {
          addNotification(
            `Service saved to ${rego}'s history — ${serviceVisibility === 'operator' ? 'visible to the van’s driver' : 'admin-only'} — plus filed in Documents`,
            'success');
        } else {
          addNotification(`Filed in Documents, but the service-history record failed: ${svc.error} — add it on the Service page`, 'error');
        }
      } else {
        addNotification(`${category} saved to ${rego} — admin-only`, 'success');
      }
      clearScan();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
      // Straight on to the next file in the batch, read and ready to check.
      if (queue.length) {
        const [next, ...rest] = queue;
        setQueue(rest);
        acceptFile(next);
        addNotification(`Next: ${next.name}${rest.length ? ` · ${rest.length} more after this` : ''}`, 'success');
      }
      await loadDocs();
    } catch (e) {
      addNotification(`Error: ${e.message}`, 'error');
    } finally {
      setSavingDoc(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.label || doc.category}" for ${rego}? This removes the file permanently.`)) return;
    try {
      const d = await api({ action: 'deleteVehicleDocument', id: doc.id });
      if (d.success) { addNotification('Document deleted', 'success'); setDocs(prev => prev.filter(x => x.id !== doc.id)); }
      else addNotification(`Delete failed: ${d.error || 'unknown error'}`, 'error');
    } catch (e) {
      addNotification(`Error: ${e.message}`, 'error');
    }
  };

  // Reopen a filed condition report for editing (loads its saved structured data).
  const editConditionReport = async (doc) => {
    try {
      const d = await api({ action: 'getConditionReport', docId: doc.id });
      if (d.success && d.report) setCrOpen({ existing: { docId: doc.id, report: d.report } });
      else addNotification('This report was created before editing was available — please create a new one.', 'error');
    } catch (e) { addNotification(`Error: ${e.message}`, 'error'); }
  };

  const vehicleLabel = [activeVehicle.year, activeVehicle.make, activeVehicle.model].filter(Boolean).join(' ') || 'Vehicle';
  const leaseHint = useMemo(() => computeWeeklyLease(profile.purchasePrice), [profile.purchasePrice]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ width: 580, maxWidth: '100%', background: 'var(--hp-bg)', borderLeft: '1px solid var(--hp-border)', height: '100%', overflowY: 'auto', padding: 28 }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 4 }}>Fleet · Documents</div>
            {pickerMode ? (
              <h3 style={{ margin: '0 0 6px', fontSize: '1.15rem' }}>Scan a document</h3>
            ) : (
              <h3 style={{ margin: '0 0 6px', fontSize: '1.15rem' }}>{vehicleLabel} — <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-gold)' }}>{rego}</span></h3>
            )}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', color: 'var(--hp-green)', fontWeight: 600 }}>
              <ShieldCheck size={13} /> Private — admin only
            </span>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Picker mode — scan first, or assign to a vehicle by hand. OCR auto-selects. */}
        {pickerMode && (
          <div style={{ marginBottom: 18, padding: '12px 14px', background: 'var(--hp-surface)', borderRadius: 10, border: `1px solid ${rego ? 'var(--hp-green)' : 'var(--hp-border)'}` }}>
            <label style={labelStyle}>Assign to vehicle</label>
            <select
              className="input"
              style={inputStyle}
              value={rego}
              onChange={e => setSelectedVehicle(vehicleOptions.find(v => (v.rego || '').toUpperCase() === e.target.value) || null)}
            >
              <option value="">— scan a document to detect, or choose —</option>
              {vehicleOptions.map(v => (
                <option key={v.rego} value={(v.rego || '').toUpperCase()}>
                  {v.rego} — {[v.year, v.make, v.model].filter(Boolean).join(' ') || 'Vehicle'}
                </option>
              ))}
            </select>
            <div style={{ fontSize: '0.7rem', color: 'var(--hp-muted)', marginTop: 6 }}>
              {rego
                ? `Filing under ${rego}. Scan or attach the document below.`
                : 'Scan the document below — the OCR reads the rego and assigns it automatically.'}
            </div>
          </div>
        )}

        {/* Condition report — capture handover condition (photos + notes) */}
        {(!pickerMode || rego) && (
          <button className="btn btn-secondary" style={{ width: '100%', marginBottom: 18, justifyContent: 'center' }} onClick={() => setCrOpen({ existing: null })}>
            <ShieldCheck size={14} /> New condition report (handover)
          </button>
        )}

        {/* ── 1. Vehicle economics ─────────────────────────────────────────── */}
        {(!pickerMode || rego) && (<>
        <SectionTitle n={1} title="Vehicle details" hint="auto-filled by OCR · editable" />
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Purchase price (AUD)</label>
            <input className="input" style={inputStyle} inputMode="decimal" value={profile.purchasePrice}
              onChange={e => setP('purchasePrice', e.target.value)} placeholder="e.g. 48000" />
          </div>
          <div>
            <label style={labelStyle}>Purchase date</label>
            <input type="date" className="input" style={inputStyle} value={profile.purchaseDate}
              onChange={e => setP('purchaseDate', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Start odometer (km)</label>
            <input className="input" style={inputStyle} inputMode="numeric" value={profile.startKms}
              onChange={e => setP('startKms', e.target.value)} placeholder="e.g. 12" />
          </div>
          <div>
            <label style={labelStyle}>Service interval</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input className="input" style={inputStyle} inputMode="numeric" value={profile.serviceIntervalKm}
                onChange={e => setP('serviceIntervalKm', e.target.value)} placeholder="km" />
              <input className="input" style={inputStyle} inputMode="numeric" value={profile.serviceIntervalMonths}
                onChange={e => setP('serviceIntervalMonths', e.target.value)} placeholder="months" />
            </div>
          </div>
        </div>

        <div style={{ marginTop: 12, padding: '12px 14px', background: 'var(--hp-surface)', borderRadius: 10, border: '1px solid var(--hp-border)' }}>
          <label style={{ ...labelStyle, color: 'var(--hp-green)' }}><DollarSign size={11} style={{ verticalAlign: -1 }} /> Suggested driver lease fee (per week)</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input className="input" style={{ ...inputStyle, fontWeight: 700, fontSize: '1rem' }} inputMode="decimal" value={profile.suggestedLeaseFee}
              onChange={e => setP('suggestedLeaseFee', e.target.value)} placeholder="e.g. 245" />
            <span style={{ fontSize: '0.78rem', color: 'var(--hp-muted)', whiteSpace: 'nowrap' }}>/ week</span>
            {leaseHint > 0 && Number(profile.suggestedLeaseFee) !== leaseHint && (
              <button type="button" className="btn btn-ghost btn-sm" title="Apply the amortised estimate (purchase ÷ 260 weeks + $60/wk running costs)"
                onClick={() => setP('suggestedLeaseFee', leaseHint)} style={{ whiteSpace: 'nowrap' }}>
                <Sparkles size={12} /> {fmtMoney(leaseHint)}
              </button>
            )}
          </div>
          <textarea className="input" style={{ ...inputStyle, marginTop: 8, minHeight: 46, resize: 'vertical', fontSize: '0.8rem' }}
            value={profile.leaseFeeNotes} onChange={e => setP('leaseFeeNotes', e.target.value)}
            placeholder="How the fee was worked out (OCR fills this in)…" />
        </div>

        <button className="btn btn-secondary" style={{ width: '100%', marginTop: 12 }} onClick={handleSaveProfile} disabled={savingProfile}>
          <Save size={14} /> {savingProfile ? 'Saving…' : 'Save vehicle details'}
        </button>
        </>)}

        {/* ── 2. Scan / add a document ─────────────────────────────────────── */}
        <SectionTitle n={2} title="Scan a document" hint="rego · purchase · insurance · finance" />

        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          style={{
            border: `2px dashed ${dragOver ? 'var(--hp-gold)' : pdfFile ? 'var(--hp-green)' : 'var(--hp-gold)66'}`,
            borderRadius: 10, padding: '16px 14px', textAlign: 'center', cursor: 'pointer',
            background: dragOver ? 'var(--hp-surface)' : 'transparent', transition: 'border-color .15s, background .15s',
          }}
        >
          {pdfFile ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: '0.8rem', color: 'var(--hp-text)' }}>
              {extracting
                ? <Loader size={14} color="var(--hp-gold)" style={{ animation: 'spin 1s linear infinite' }} />
                : <Paperclip size={14} color="var(--hp-green)" />}
              <span style={{ fontWeight: 600 }}>{pdfFile.name}</span>
              <span style={{ color: 'var(--hp-muted)', fontSize: '0.72rem' }}>({(pdfFile.size / 1024 / 1024).toFixed(2)} MB)</span>
              <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); clearScan(); }} title="Remove"><X size={12} /></button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: '0.82rem', color: 'var(--hp-dim)' }}>
                <FileText size={15} color="var(--hp-gold)" />
                <span><strong style={{ color: 'var(--hp-text)' }}>Drop PDFs or photos here</strong> — or click to browse</span>
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--hp-muted)', marginTop: 5 }}>
                up to {MAX_DOC_MB} MB each · drop several at once · OCR reads each & files it · stored privately on save
              </div>
            </>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept="application/pdf,image/*" multiple style={{ display: 'none' }} onChange={handleFilePick} />

        {queue.length > 0 && (
          <div style={{ marginTop: 8, border: '1px solid var(--hp-border)', borderRadius: 8, padding: '8px 10px' }}>
            <div style={{ fontSize: '.7rem', color: 'var(--hp-muted)', marginBottom: 6, fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
              Waiting — {queue.length} file{queue.length === 1 ? '' : 's'}, filed after this one
            </div>
            {queue.map((f, i) => (
              <div key={`${f.name}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.78rem', color: 'var(--hp-dim)', padding: '2px 0' }}>
                <Paperclip size={12} color="var(--hp-muted)" />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                <span style={{ fontSize: '.7rem', color: 'var(--hp-muted)' }}>{(f.size / 1024 / 1024).toFixed(2)} MB</span>
                <button className="btn btn-ghost btn-sm" title="Remove from the queue"
                  onClick={() => setQueue(q => q.filter((_, j) => j !== i))}><X size={11} /></button>
              </div>
            ))}
          </div>
        )}

        {/* Mobile camera scan — fires the device camera (side-button shutter) */}
        <label className="btn btn-secondary btn-sm" style={{ marginTop: 8, width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer' }}>
          <Camera size={14} /> {scanPages.length ? `Add another page (page ${scanPages.length + 1})` : 'Scan with camera'}
          <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleScanPick} />
        </label>
        {scanPages.length > 0 && (
          <>
            <div style={{ fontSize: '0.72rem', color: 'var(--hp-muted)', marginTop: 6, textAlign: 'center' }}>
              {scanPages.length} page{scanPages.length > 1 ? 's' : ''} scanned — combined into one PDF on save.
            </div>
            <button type="button" className="btn btn-primary btn-sm" disabled={extracting || !pdfFile}
              onClick={() => pdfFile && autoClassify(pdfFile)}
              style={{ marginTop: 8, width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <FileText size={14} /> Read all {scanPages.length} page{scanPages.length > 1 ? 's' : ''}
            </button>
          </>
        )}
        {extracting && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: '0.75rem', color: 'var(--hp-gold)' }}>
            <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> Reading the document & filling details…
          </div>
        )}
        {!extracting && extractedNote && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 8, fontSize: '0.75rem', color: 'var(--hp-green)' }}>
            <CheckCircle size={12} style={{ flexShrink: 0, marginTop: 2 }} /> {extractedNote}
          </div>
        )}

        {/* Category + label + save */}
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 12, marginTop: 14 }}>
          <div>
            <label style={labelStyle}>File under</label>
            <select className="input" style={inputStyle} value={category} onChange={e => setCategory(e.target.value)}>
              {DOC_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Label</label>
            <input className="input" style={inputStyle} value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. 2025 Rego Renewal" />
          </div>
        </div>

        {/* Service → also files onto the vehicle's service history; ask who may see it. */}
        {category === 'Service' && (
          <div style={{ marginTop: 12, padding: '12px 14px', border: '1px solid var(--hp-border)', borderRadius: 10, background: 'var(--hp-surface-2, rgba(0,0,0,0.02))' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--hp-text)', marginBottom: 4 }}>
              This is a service — it will also go on {rego || 'the vehicle'}’s service history
            </div>
            <div style={{ fontSize: '0.76rem', color: 'var(--hp-muted)', marginBottom: 10 }}>
              Who should be able to see this service record?
            </div>
            <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                { v: 'admin', title: 'Hidden — admin only', desc: 'Only office admins can see it.' },
                { v: 'operator', title: 'Exposed for viewing', desc: 'Admin + the van’s current driver.' },
              ].map(o => {
                const on = serviceVisibility === o.v;
                return (
                  <button key={o.v} type="button" onClick={() => setServiceVisibility(o.v)}
                    style={{ textAlign: 'left', padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                      border: `1.5px solid ${on ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
                      background: on ? 'var(--hp-gold-soft, rgba(212,175,55,0.12))' : 'transparent' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--hp-text)' }}>{o.title}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--hp-muted)', marginTop: 2 }}>{o.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <button className="btn btn-primary" style={{ width: '100%', marginTop: 12 }} onClick={handleSaveDoc} disabled={savingDoc || !pdfFile}>
          {savingDoc ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
            : savedFlash ? <><CheckCircle size={14} /> Saved</>
            : category === 'Service' ? <><Lock size={14} /> Save — file in Documents + service history</>
            : <><Lock size={14} /> Save document (admin-only)</>}
        </button>

        {/* ── 3. Stored documents ──────────────────────────────────────────── */}
        {(!pickerMode || rego) && (<>
        <SectionTitle n={3} title={`Stored documents${docs.length ? ` (${docs.length})` : ''}`} />
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--hp-muted)', fontSize: '0.82rem', padding: '10px 0' }}>
            <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
          </div>
        ) : docs.length === 0 ? (
          <div style={{ padding: '18px 14px', textAlign: 'center', color: 'var(--hp-muted)', fontSize: '0.82rem', border: '1px dashed var(--hp-border)', borderRadius: 10 }}>
            No documents yet — scan the rego papers or purchase invoice above.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {docs.map(doc => (
              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 10 }}>
                <FileText size={16} color="var(--hp-dim)" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <CategoryBadge category={doc.category} />
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--hp-text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.label || doc.filename}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--hp-muted)' }}>
                    {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                    {doc.uploadedBy ? ` · ${doc.uploadedBy}` : ''}
                  </div>
                </div>
                {doc.category === 'Condition Report' && (
                  <button className="btn btn-ghost btn-sm" onClick={() => editConditionReport(doc)} title="Edit this condition report" style={{ flexShrink: 0 }}>
                    <Pencil size={13} /> Edit
                  </button>
                )}
                <a href={doc.viewUrl} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" title="View (admin-only)" style={{ flexShrink: 0 }}>
                  <ExternalLink size={13} /> View
                </a>
                <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(doc)} title="Delete" style={{ flexShrink: 0, color: 'var(--hp-red)' }}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
        </>)}

        <div style={{ marginTop: 18, fontSize: '0.7rem', color: 'var(--hp-muted)', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <Lock size={12} style={{ flexShrink: 0, marginTop: 2 }} />
          Documents are stored owner-only in Drive and only ever shown to a signed-in admin — drivers using this vehicle on the shared device can’t see them.
        </div>
      </div>

      {crOpen && (
        <ConditionReportDrawer
          vehicle={activeVehicle} currentUser={currentUser}
          existing={crOpen.existing}
          onClose={() => setCrOpen(null)}
          onSaved={loadDocs}
          addNotification={addNotification}
        />
      )}
    </div>
  );
}
